import React, {useContext, useEffect, useState} from "react";
import {PageTitle} from "../../../_metronic/layout/core";
import BedRockContext from "../../BedRockContext";
import {defaultList} from "../generic/InitialStates";
import {
    GENERIC_SETTING_TYPE_CURRENCY,
    GENERIC_SETTINGS
} from "../generic/Constants";
import {GenericTable} from "../generic/table/GenericTable";
import {HeaderColumnFormatter} from "../../column-formatters/HeaderColumnFormatter";
import {ActionsColumnFormatter} from "../../column-formatters/ActionsColumnFormatter";
import {OtherSettingsAddDialog} from "./OtherSettingsAddDialog";
import {IDMapFormatter} from "../../column-formatters/IDMapFormatter";

export function OtherSettings() {
    const myData = useContext(BedRockContext);
    const [reload, setReload] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [selectedSettingForEdit, setSelectedSettingForEdit] = useState(null);

    function reloadData() {
        setReload(reload + 1);
    }

    const settings = [
        {
            name: "Currency",
            icon: "dollar",
            description: "Select the default currency for the system",
            type: GENERIC_SETTING_TYPE_CURRENCY,
            settingFieldName: "Currency",
            allowAllClasses: true,
            allowMultiSelect: false,
            settingFieldValues: [
                {id: 1, name: "$ - US Dollar (USD)"},
                {id: 2, name: "€ - Euro (EUR)"},
                {id: 3, name: "₹ - Indian Rupee (INR)"},
                {id: 4, name: "£ - British Pound (GBP)"},
                {id: 5, name: "¥ - Japanese Yen (JPY)"},
                {id: 6, name: "₽ - Russian Ruble (RUB)"},
                {id: 7, name: "¥ - Chinese Yuan (CNY)"},
                {id: 8, name: "₣ - Swiss Franc (CHF)"},
                {id: 9, name: "$ - Canadian Dollar (CAD)"},
                {id: 10, name: "$ - Australian Dollar (AUD)"},
            ],
        }
    ];

    const [selectedSetting, setSelectedSetting] = useState(settings[0]);

    const profileBreadCrumbs = [
        {
            title: 'Home',
            path: '/dashboard',
            isSeparator: false,
            isActive: false,
        },
        {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
        },
    ];

    useEffect(() => {
        myData.setBaseData({
            toolBarButtons: [{
                name: "ADD NEW",
                color: "primary",
                icon: "fa fa-plus",
                onClick: addButtonClicked,
            },]
        })
    }, [reload]);

    function settingsClicked(setting) {
        setSelectedSetting(setting)
        reloadData();
    }

    function addButtonClicked() {
        setShowModal(true);
    }

    function openDeleteDialog(row) {
        // Function is currently empty - will need implementation when delete functionality is required
        const id = row.id || row;
        console.log("Delete requested for ID:", id);
    }
    
    function openEditDialog(row) {
        setShowModal(true);
        // Here we would typically fetch the current setting and pass it to the modal
        // For now, we'll just pass the row data
        setSelectedSettingForEdit(row);
    }

    const getColumnsForSetting = (setting) => {
        let actionButtons = [];
        
        // For currency, only show Edit button, not Delete
        if (setting.type === GENERIC_SETTING_TYPE_CURRENCY) {
            actionButtons = [
                {
                    name: "Edit",
                    color: "primary",
                    icon: "pencil",
                    method: openEditDialog
                }
            ];
        } else {
            actionButtons = [
                {
                    name: "Delete",
                    color: "danger",
                    icon: "trash-alt",
                    method: openDeleteDialog
                }
            ];
        }
        
        const baseColumns = [
            {
                dataField: "settingValue",
                text: "Value",
                formatter: IDMapFormatter,
                formatExtraData: {
                    array: setting.settingFieldValues
                }
            },
            {
                dataField: "action",
                text: "Actions",
                formatter: ActionsColumnFormatter,
                formatExtraData: {
                    buttons: actionButtons,
                },
                classes: "text-right pr-0",
                headerClasses: "text-right pr-3",
                style: {
                    minWidth: "100px",
                },
            }
        ];
        
        // Add Class column for all settings except Currency
        if (setting.type !== GENERIC_SETTING_TYPE_CURRENCY) {
            return [
                {
                    dataField: "className",
                    text: "Class",
                    formatter: HeaderColumnFormatter
                },
                ...baseColumns
            ];
        }
        
        return baseColumns;
    };
    
    const columns = getColumnsForSetting(selectedSetting);

    const settingsMenuMarkup = settings.map((setting) => {
        return (
            <div className="menu-item mb-3" onClick={() => settingsClicked(setting)} key={setting.name}>
                <span className={`menu-link ${selectedSetting.name === setting.name ? 'active' : ''}`}>
                    <span className="menu-icon">
                        <i className={`ki-duotone ki-${setting.icon} fs-2 me-3`}>
                                <span className="path1"/>
                                <span className="path2"/>
                                <span className="path3"/>
                                <span className="path4"/>
                                <span className="path5"/>
                        </i>
                    </span>
                    <span className="menu-title fw-bold">{setting.name}</span>
                </span>
            </div>
        )
    });

    function settingContentMarkup() {
        return (
            <div className="flex-lg-row-fluid ms-lg-7 ms-xl-10">
                <div className="card">
                    <div className="card-header mt-6">
                        <div className="card-title flex-column">
                            <h3 className="fw-bold mb-1">{selectedSetting.name}</h3>
                            <div className="fs-6 fw-semibold text-gray-400">{selectedSetting.description}</div>
                        </div>
                        <div className="card-toolbar">
                            <i className={`ki-duotone ki-${selectedSetting.icon} fs-3x me-3 text-primary`}>
                                <span className="path1"/>
                                <span className="path2"/>
                                <span className="path3"/>
                                <span className="path4"/>
                                <span className="path5"/>
                            </i>
                        </div>
                    </div>

                    <div className="card-body p-10">
                        <GenericTable
                            pageSize={100}
                            reload={reload}
                            defaultData={defaultList}
                            columns={columns}
                            entityId={GENERIC_SETTINGS}
                            fixedFilterColumn={"settingType"}
                            fixedFilterData={selectedSetting.type}
                        />
                    </div>
                </div>
            </div>
        )
    }

    const handleClose = () => {
        setShowModal(false);
        setSelectedSettingForEdit(null);
        reloadData();
    };

    return <>
        <PageTitle breadcrumbs={profileBreadCrumbs}>Other Settings</PageTitle>
        <div>
            <div className="d-flex flex-column flex-lg-row">
                <div
                    className="d-none d-lg-flex flex-column flex-lg-row-auto w-100 w-lg-275px">
                    <div className="card card-flush mb-0">
                        <div className="card-body">
                            <div
                                className="menu menu-column menu-rounded menu-state-bg menu-state-title-primary menu-state-icon-primary menu-state-bullet-primary mb-10">
                                {settingsMenuMarkup}
                            </div>
                        </div>
                    </div>
                </div>

                {settingContentMarkup()}
            </div>

        </div>
        <OtherSettingsAddDialog
            show={showModal}
            handleClose={handleClose}
            name={selectedSetting.name}
            type={selectedSetting.type}
            allowMultiSelect={selectedSetting.allowMultiSelect}
            allowAllClasses={selectedSetting.allowAllClasses}
            settingFieldName={selectedSetting.settingFieldName}
            settingFieldValues={selectedSetting.settingFieldValues}
            editMode={selectedSettingForEdit !== null}
            settingToEdit={selectedSettingForEdit}/>

    </>
}